import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * View state that lives in the URL rather than in component state.
 *
 * WHY THIS EXISTS
 * Every list screen kept its tab, search, filters and sort in `useState`. That made a
 * whole class of things impossible, and the symptom recruiters actually hit was this:
 * open a candidate's report, press Back, and you land on the job's Overview tab with
 * the filters and sort reset — so you cannot get back to the same candidates of the
 * same job. Fixing the back link alone would not have solved it, because there was no
 * state to return TO.
 *
 * Putting it in the URL fixes that and three other things for free: a filtered view can
 * be bookmarked, it can be pasted to a colleague, and the browser's own Back button
 * starts behaving the way people expect.
 *
 * Defaults are never written to the URL, so a clean screen keeps a clean address bar.
 */
export function useUrlState<T extends string>(
  key: string,
  fallback: T,
  allowed?: readonly T[],
): [T, (next: T | ((prev: T) => T)) => void] {
  const [params, setParams] = useSearchParams();

  const raw = params.get(key);
  const value = useMemo<T>(() => {
    if (raw === null) return fallback;
    if (allowed && !allowed.includes(raw as T)) return fallback;
    return raw as T;
  }, [raw, fallback, allowed]);

  // Accepts a value or an updater, matching useState so existing call sites that do
  // `setSortDir(d => d === 'asc' ? 'desc' : 'asc')` keep working unchanged.
  const set = useCallback(
    (next: T | ((prev: T) => T)) => {
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          const current = (() => {
            const r = p.get(key);
            if (r === null) return fallback;
            if (allowed && !allowed.includes(r as T)) return fallback;
            return r as T;
          })();
          const resolved = typeof next === 'function' ? (next as (p: T) => T)(current) : next;
          // Omit defaults so the URL stays readable and two equivalent views share one address.
          if (resolved === fallback) p.delete(key);
          else p.set(key, resolved);
          return p;
        },
        // Filter changes are not navigation events — replace so Back doesn't have to
        // walk every keystroke of a search box.
        { replace: true },
      );
    },
    [key, fallback, allowed, setParams],
  );

  return [value, set];
}

/** Boolean flavour: present-and-"1" is true. */
export function useUrlFlag(
  key: string,
  fallback = false,
): [boolean, (next: boolean | ((prev: boolean) => boolean)) => void] {
  const [raw, setRaw] = useUrlState<'0' | '1'>(key, fallback ? '1' : '0', ['0', '1'] as const);
  const set = useCallback(
    (next: boolean | ((prev: boolean) => boolean)) => {
      setRaw((prev) => {
        const resolved = typeof next === 'function' ? next(prev === '1') : next;
        return resolved ? '1' : '0';
      });
    },
    [setRaw],
  );
  return [raw === '1', set];
}

/**
 * The current location as an opaque return ticket.
 *
 * Handed to a detail page so it can offer an exact way back — including the tab,
 * filters and sort the user had set — instead of guessing at a hardcoded list route.
 */
export function encodeReturnTo(pathname: string, search: string): string {
  return encodeURIComponent(pathname + (search || ''));
}

/**
 * Decode a return ticket, refusing anything that could navigate off-site.
 *
 * Same rule as the post-login redirect: one leading slash, not followed by another
 * slash or a backslash. A backslash matters because browsers normalise `/\evil.com`
 * into the protocol-relative `//evil.com`.
 */
export function decodeReturnTo(raw: string | null): string | null {
  if (!raw) return null;
  let value: string;
  try {
    value = decodeURIComponent(raw);
  } catch {
    return null;
  }
  const trimmed = value.replace(/^[ - ]+/, '').replace(/[ - ]+$/, '');
  if (!/^\/(?![/\\])[^\s]*$/.test(trimmed)) return null;
  return trimmed;
}
